/**
 * member-replies.js — 會員「診所回復通知」展開收合 + 進頁靜默標已讀
 *
 * 對照 HTML（src/pages/member/replies.html）—— 一致性規格（uiux 2026-06-10）未讀 / 已讀「兩卡」
 * 皆收合 3 行 + 展開鈕（reply body 各帶唯一 id：未讀 reply-body-1 / 已讀 reply-body-2）：
 *   <ul class="member-reply-list" data-member-replies>
 *     <li><article class="card reply-notice reply-notice--unread">
 *       …<div class="reply-notice__reply" id="reply-body-1">…</div>
 *       <button class="reply-notice__toggle" data-reply-toggle
 *               aria-expanded="false" aria-controls="reply-body-1">…</button>
 *     </article></li>
 *     <li><article class="card reply-notice reply-notice--read">
 *       …<div class="reply-notice__reply" id="reply-body-2">…</div>
 *       <button class="reply-notice__toggle" data-reply-toggle
 *               aria-expanded="false" aria-controls="reply-body-2">
 *         <span data-reply-toggle-label>展開</span><svg class="reply-notice__toggle-chevron">…</svg>
 *       </button>
 *     </article></li>
 *   </ul>
 *
 * 功能（MEMBER-SPEC §B.3 / §C4，收合一致性規格 2026-06-10）：
 *   - 通知展開/收合（未讀 / 已讀共用）：CSS 以 .reply-notice__reply 的 grid-template-rows
 *     minmax(3 行)↔1fr 控高（member.css，含 reduced-motion 直接到位）；本 JS 只切 .is-expanded
 *     + aria-expanded + 鈕文字 label（展開↔收合，只動 [data-reply-toggle-label] span 不碰 chevron SVG）。
 *   - 進頁靜默標已讀：頁面載入後，未讀通知視為「已讀」記錄（語意上會讓下次造訪的未讀數歸零），
 *     但**不改動本次造訪的視覺**（未讀仍 teal 強調、收合 3 行排前，符合 §C4「未讀排前 teal 強調」）。
 *     單向通知不回信，故此處只發出一次 hook（CustomEvent），供未來接後端 PATCH /replies/read；
 *     無後端時為 no-op，不造成視覺跳動（「靜默」即不打擾當前畫面）。
 *
 * 載入：由 /src/admin.js 中央 import 鏈 auto-init（會員頁殼亦走 admin.js）。
 *   無對應 DOM（[data-member-replies] 不存在）即 no-op，可安全在所有會員頁載入。
 *
 * TODO(backend): 進頁靜默標已讀目前只 dispatch CustomEvent('member:replies-read')，無 listener 即 no-op；
 *   接後端時掛 listener 打 PATCH /replies/read（見 silentMarkRead）。
 */

function initToggles(root) {
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-reply-toggle]');
    if (!btn) return;

    const region = document.getElementById(btn.getAttribute('aria-controls'));
    if (!region) return;

    const expanded = btn.getAttribute('aria-expanded') === 'true';
    const next = !expanded;
    btn.setAttribute('aria-expanded', next ? 'true' : 'false');
    region.classList.toggle('is-expanded', next);
    // 只更新文字 label span（保留 chevron SVG，aria-expanded 翻轉 chevron 由 CSS 控）；
    // 無 label span（舊結構）則退回整鈕 textContent，向後相容。
    const label = btn.querySelector('[data-reply-toggle-label]');
    if (label) {
      label.textContent = next ? '收合' : '展開';
    } else {
      btn.textContent = next ? '收合' : '展開';
    }
  });
}

// 短內容免鈕：reply 卡內文若不足收合高（容得下全文，無被裁第 4 行起），不該出現「展開」鈕。
// 一致性規格（uiux 2026-06-10）：未讀 / 已讀「一律」收合 3 行 + 展開鈕，故量測涵蓋全部
// .reply-notice（非只 --read）；未讀卡內文 ≤3 行同樣自動隱鈕。
// 量測 token-agnostic（不在 JS 重算 99.5px）：
//   - .reply-notice__reply（grid 收合容器）clientHeight = 實際收合高（CSS calc 算出的 3 行高）
//   - .reply-notice__reply-inner（overflow:hidden）scrollHeight = 內文完整自然高（不受裁切影響）
// 雙向 toggle（resize 重量測需求）：依當下量測「雙向重設」鈕的 hidden，不是只隱藏——
//   - inner 全高 ≤ 收合容器高（+1px 容差吸收 sub-pixel）→ 全文已可見 → hidden=true（免鈕）
//   - inner 全高 > 收合容器高 → 內文被裁 → hidden=false（補回鈕）
// 視窗從寬拖到窄後，內文回流變高、原本免鈕的卡需補回鈕；反向亦然，故必須雙向。
// 已展開（.is-expanded）的卡 reply 高 = 全展開，量測無意義 → 跳過（不動它的鈕，延續既有邏輯）。
// 收合態（含 hidden=false 暫顯）下量測：is-expanded 已被排除，reply 維持 CSS calc 收合高，量測有效。
// 首次須在 fonts ready 後跑（web font 換上會改行高 → scrollHeight 變動，提早量會誤判）。
function refreshTogglesForContent(list) {
  const cards = list.querySelectorAll('.reply-notice');
  cards.forEach((card) => {
    const reply = card.querySelector('.reply-notice__reply');
    const inner = card.querySelector('.reply-notice__reply-inner');
    const toggle = card.querySelector('[data-reply-toggle]');
    if (!reply || !inner || !toggle) return;
    if (reply.classList.contains('is-expanded')) return; // 已展開不量、不動其鈕
    const collapsedH = reply.clientHeight;
    if (collapsedH <= 0) return; // 隱藏 / 未佈局，放棄（保守保留鈕現況）
    // 雙向：fits → 免鈕(hidden=true)；overflows → 補鈕(hidden=false)。
    toggle.hidden = inner.scrollHeight <= collapsedH + 1;
  });
}

// 進頁靜默標已讀：收集未讀通知 id（demo 無資料 id 時以 index 佔位），發一次 hook。
// 不改 --unread class、不收合 —— 維持本次造訪畫面，僅作為後端標記點。
function silentMarkRead(list) {
  const unread = Array.from(list.querySelectorAll('.reply-notice--unread'));
  if (!unread.length) return;
  const ids = unread.map((el, i) => el.dataset.replyId || `unread-${i}`);
  // 供未來後端串接：don 本波無 backend，dispatch 後無 listener 即靜默結束（無副作用）。
  // TODO(backend): 掛 listener 對 member:replies-read 打 PATCH /replies/read，通知列表改撈 API — 見 docs/BACKEND_HANDOFF.md#member-replies-read
  document.dispatchEvent(new CustomEvent('member:replies-read', { detail: { ids } }));
}

function init(root = document) {
  const list = root.querySelector('[data-member-replies]');
  if (!list) return; // no-op：非 replies 頁
  initToggles(list);
  silentMarkRead(list);
  // 短內容免鈕量測需等 web font 換上（行高定案）才準；fonts API 不存在則退回直接量。
  // 用 document.fonts（字型載入是 document 全域，與查詢 scope root 無關）。
  const fonts = typeof document !== 'undefined' ? document.fonts : null;
  if (fonts && typeof fonts.ready?.then === 'function') {
    fonts.ready.then(() => refreshTogglesForContent(list));
  } else {
    refreshTogglesForContent(list);
  }

  // resize 重量測（robustness）：視窗寬度變化→內文回流→收合高下能容納的行數變→需重判免鈕/補鈕。
  // 只在 fonts.ready 跑一次會讓「寬拖窄」後維持初次隱藏態 → 末行被硬裁且無鈕。debounce 180ms
  // 避免 resize 連發狂量；refreshTogglesForContent 本身雙向（fits 免鈕 / overflows 補鈕）。
  let resizeTimer = null;
  window.addEventListener('resize', () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      resizeTimer = null;
      refreshTogglesForContent(list);
    }, 180);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}
